import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useKeydown from "@hooks/useKeydown";
import "./styles/VoucherModal.scss";
import { InoButton, InoRow, InoKeyboard } from "@ino-ui/tv";
import { useSelector, useDispatch } from "react-redux";
import { selectCtrl, setCtrl } from "@app/global";
import {
  registerDeviceWithCode,
  loginUser,
  getlLauncherUser,
} from "@server/requests";
import LOCAL_STORAGE from "@utils/localStorage.js";

export default function VoucherModal({ isVisible, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [voucherCode, setVoucherCode] = useState("");
  const [activeField, setActiveField] = useState("input"); // "input", "submit", "close"
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const dispatch = useDispatch();
  const ctrl = useSelector(selectCtrl);

  useEffect(() => {
    if (isVisible) {
      setActiveField("input");
      dispatch(setCtrl("inp"));
      setShowKeyboard(false);
    }
  }, [isVisible, dispatch]);

  const handleKeyInput = (key) => {
    if (ctrl !== "inp") return;

    if (
      key === "Backspace" ||
      key === "Delete" ||
      (window.tizen && key === "")
    ) {
      setVoucherCode((prev) => prev.slice(0, -1));
      return;
    }

    if (/^[a-zA-Z0-9]$/.test(key)) {
      setVoucherCode((prev) => prev + key.toUpperCase());
    }
  };

  const handleKeyboardInput = (character) => {
    // Handle special keyboard actions
    if (character === "Backspace" || character === "Delete") {
      setVoucherCode((prev) => prev.slice(0, -1));
      return;
    }

    if (character === "Clear") {
      setVoucherCode("");
      return;
    }

    // For regular characters, append to existing value
    if (character && typeof character === "string" && character.length === 1) {
      setVoucherCode((prev) => prev + character.toUpperCase());
    }
  };

  const handleKeyboardClose = () => {
    setShowKeyboard(false);
  };

  const handleKeyboardSubmit = () => {
    setShowKeyboard(false);
    setActiveField("submit");
    dispatch(setCtrl("nav"));
  };

  const openKeyboard = () => {
    setShowKeyboard(true);
  };

  const handleSubmit = async (code) => {
    if (!code) return;

    setError(null); // Clear any previous errors
    setIsLoading(true); // Add a loading state to prevent duplicate submissions

    try {
      const res = await registerDeviceWithCode(code);
      const response = JSON.parse(res);

      if (!response.error) {
        const resLogin = await loginUser({
          device_id: LOCAL_STORAGE.MAC_ADDRESS.GET(),
        });

        const loginResponse = JSON.parse(resLogin);

        if (!loginResponse.error) {
          const userResponse = await getlLauncherUser({
            device_id: LOCAL_STORAGE.MAC_ADDRESS.GET(),
          });

          const parsedUserResponse = JSON.parse(userResponse);

          if (!parsedUserResponse.error) {
            onSubmit(code);
            onClose(true);
          } else {
            setError(t("Failed to fetch user data. Please try again."));
          }
        } else {
          setError(t("Failed to log in. Please try again."));
        }
      } else {
        setError(t("Failed to register device. Please try again."));
      }
    } catch (err) {
      setError(t("An unexpected error occurred. Please try again."));
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  useKeydown({
    isActive: isVisible && !showKeyboard,
    down: () => {
      if (activeField === "input") {
        setActiveField("submit");
        dispatch(setCtrl("nav"));
      }
    },
    up: () => {
      if (activeField === "submit" || activeField === "close") {
        setActiveField("input");
        dispatch(setCtrl("inp"));
      }
    },
    left: () => {
      if (activeField === "submit" || activeField === "close") {
        setActiveField("submit");
      }
    },
    right: () => {
      if (activeField === "submit" || activeField === "close") {
        setActiveField("close");
      }
    },
    ok: () => {
      if (activeField === "input") {
        openKeyboard();
      } else if (activeField === "submit") {
        handleSubmit(voucherCode);
      } else if (activeField === "close") {
        onClose();
      }
    },
    handleKeyPress: handleKeyInput,
    back: () => {
      if (ctrl === "inp") {
        if (voucherCode.length === 0) {
          setActiveField("submit");
          dispatch(setCtrl("nav"));
        }
      } else {
        onClose();
      }
    },
  });

  return (
    <div className={`voucher-modal-overlay ${isVisible ? "visible" : ""}`}>
      <div className="voucher-modal">
        <div className="voucher-header">
          <h2>{t("Enter Activation Code")}</h2>
        </div>

        <div className="voucher-content">
          <div className="login-fields-ino">
            <div className="login-field-ino">
              <div
                className={`login-input-display ${activeField === "input" ? "active" : ""}`}
                onClick={openKeyboard}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && activeField === "input") {
                    openKeyboard();
                  }
                }}
              >
                <span className="login-input-text">
                  {voucherCode || t("Enter your voucher code")}
                </span>
                {activeField === "input" && (
                  <div className="cursor-blink"></div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="voucher-error">
              <p>{error}</p>
            </div>
          )}

          <InoRow
            isActive={activeField === "submit" || activeField === "close"}
            classNames="login-actions-row"
          >
            <InoButton
              isActive={activeField === "submit"}
              disabled={isLoading}
              onClick={() => handleSubmit(voucherCode)}
              size="large"
              classNames="login-submit-btn"
            >
              {isLoading ? t("Submitting...") : t("Submit")}
            </InoButton>
            <InoButton
              isActive={activeField === "close"}
              onClick={() => {
                dispatch(setCtrl("inp"));
                setActiveField();
                onClose();
              }}
              variant="outline"
              size="large"
              classNames="login-cancel-btn"
            >
              {t("Close")}
            </InoButton>
          </InoRow>
        </div>
      </div>

      {showKeyboard && (
        <InoKeyboard
          key={`keyboard-voucher-${showKeyboard ? "open" : "closed"}`}
          isOpen={showKeyboard}
          value={voucherCode}
          onChange={handleKeyboardInput}
          onClose={handleKeyboardClose}
          onSubmit={handleKeyboardSubmit}
          placeholder={t("Enter your voucher code")}
          type="text"
          title={t("Enter Activation Code")}
          classNames="login-keyboard"
        />
      )}
    </div>
  );
}
